import React, { useCallback, useEffect, useRef, useState } from 'react';
import './components/ui/LoadingSpinner/LoadingSpinner.css';
import './App.css';
import { PhotoGrid, StoryView, MediaModal, LoginScreen, UnpairedScreen } from './components';
import { MainLayout, ScrapbookHeader, SeasonContainer } from './components/layout';
import { useStoryStore, useUIStore, useAuthStore, useViewerModeStore } from './stores';
import { useSeasonsStore } from './stores/useSeasonsStore';
import { PhotoUploadModal } from './components/admin/PhotoUploadModal';
import { PhotoActionsModal } from './components/admin/PhotoActionsModal';
import { AddSeasonButton } from './components/admin/AddSeasonButton';
import { Toast } from './components/shared/Toast';
import { apiClient } from './services/apiClient';
import { useTvBackBridge } from './hooks/useTvBackBridge';
import { UNPAIRED_RETRY_MS } from './utils/tvPlayback';

// --- Main App Component ---
const App = (): React.JSX.Element => {
    const openStory = useStoryStore((state) => state.openStory);
    const { toggleEvent, getOpenEvent, toastMessage, toastType, hideToast } = useUIStore();
    const { status, restoreSession, markSignedOut } = useAuthStore();
    const isAdmitted = status === 'authenticated';
    useTvBackBridge();

    const {
        error,
        fetchPhotos,
        fetchSeasons,
        fetchAllEvents,
        syncPhotosByEventWithAllEvents,
        getOrganizedSeasonData,
        clearError,
        clearData
    } = useSeasonsStore();

    const mode = useViewerModeStore((state) => state.mode);

    const seasonCollection = getOrganizedSeasonData(mode);

    const [initialLoadState, setInitialLoadState] = useState<'loading' | 'failed' | 'complete'>('loading');
    const initEpochRef = useRef(0);

    const initializeData = useCallback(async () => {
        initEpochRef.current += 1;
        const epoch = initEpochRef.current;
        setInitialLoadState('loading');
        clearError();

        if (mode === 'family') {
            // Containers derive from the filtered manifest alone. /seasons and
            // /events return unfiltered names for admin work and must never be
            // fetched here — an event with nothing shareable would otherwise
            // reappear as a named empty placeholder.
            await fetchPhotos('family');
        } else {
            await fetchSeasons();
            await Promise.all([
                fetchPhotos('personal'),
                fetchAllEvents()
            ]);
            syncPhotosByEventWithAllEvents();
        }

        // The mode toggle stays clickable through the loading screen, so a
        // newer run may own it by now; a stale chain must not flip the verdict.
        if (epoch !== initEpochRef.current) {
            return;
        }

        const initializationError = useSeasonsStore.getState().error;
        setInitialLoadState(initializationError ? 'failed' : 'complete');
    }, [mode, clearError, fetchAllEvents, fetchPhotos, fetchSeasons, syncPhotosByEventWithAllEvents]);

    useEffect(() => {
        restoreSession();
    }, [restoreSession]);

    useEffect(() => {
        apiClient.setUnauthenticatedCallback(() => {
            markSignedOut();
            clearData();
            useUIStore.getState().closeMediaModal();
        });
        return () => apiClient.setUnauthenticatedCallback(null);
    }, [clearData, markSignedOut]);

    // A not-paired TV keeps retrying, so re-pairing (or a restored token version) recovers
    // without anyone touching the remote.
    useEffect(() => {
        if (status !== 'unpaired') {
            return;
        }

        const retryTimer = window.setInterval(restoreSession, UNPAIRED_RETRY_MS);

        return () => window.clearInterval(retryTimer);
    }, [status, restoreSession]);

    useEffect(() => {
        if (isAdmitted) {
            initializeData();
        }
    }, [initializeData, isAdmitted]);

    const handleAvatarClick = (event: string | null, season: string) => {
        if (event) {
            toggleEvent(season, event);
        }
    };

    // Session check in flight
    if (status === 'checking') {
        return <MainLayout hideWatermarks={true}><div className="loading-screen"><h2>Loading your memories...</h2></div></MainLayout>;
    }

    if (status === 'unpaired') {
        return <UnpairedScreen />;
    }

    if (status === 'anonymous') {
        return <LoginScreen />;
    }

    if (initialLoadState === 'loading') {
        return (
            <MainLayout hideWatermarks={true}>
                <div className="loading-screen">
                    <h2>📸 Loading your memories...</h2>
                    <div className="loading-spinner"></div>
                </div>
            </MainLayout>
        );
    }

    if (initialLoadState === 'failed') {
        return (
            <MainLayout>
                <div className="error-screen">
                    <h2>❌ Failed to load data</h2>
                    <p>{error}</p>
                    <button onClick={initializeData}>Retry</button>
                </div>
            </MainLayout>
        );
    }

    return (
        <>
            <MainLayout hideWatermarks={false}>
                <ScrapbookHeader />

                <PhotoGrid onShowCard={openStory} />

                <AddSeasonButton />

                {seasonCollection.seasons.map((organizedSeasonData) => (
                    <SeasonContainer
                        key={organizedSeasonData.seasonId}
                        seasonId={organizedSeasonData.seasonId}
                        seasonEvents={organizedSeasonData.events}
                        onAvatarClick={handleAvatarClick}
                        openEvent={getOpenEvent(organizedSeasonData.seasonId) || null}
                        organizedSeasonData={organizedSeasonData}
                    />
                ))}

                <StoryView />
                <MediaModal />
                <PhotoUploadModal />
                <PhotoActionsModal />

                {error && (
                    <div className="error-banner">
                        Error: {error}
                        <button onClick={clearError}>×</button>
                    </div>
                )}
            </MainLayout>

            {/* Centralized toast rendering - OUTSIDE MainLayout for proper viewport positioning */}
            {toastMessage && (
                <Toast
                    message={toastMessage}
                    type={toastType}
                    onClose={hideToast}
                />
            )}
        </>
    );
};

export default App;
